import React from "react";
import classNames from "classnames";
import { Button, Heading } from "@r360/library";
import PersonalInformation, { TPersonalInformationGuest } from "../PersonalInformation/PersonalInformation";
import useTranslate from "../../hooks/useTranslate";
import { TGuest } from "../../store/types";

type TCustomerOptions = {
    show_keycard_number?: boolean;
    show_keycard_image?: boolean;
    show_weight?: boolean;
    show_height?: boolean;
    show_shoesize?: boolean;
    show_headsize?: boolean;
};

type GuestFormProps = {
    guestToEdit: string | null;
    newGuestIsOpen: boolean;
    guests: [string, TGuest][];
    updatingGuestInformationInProgress: boolean;
    creatingNewGuestGuestInProgress: boolean;
    guestRefs: Record<string, { form: React.RefObject<any> }>;
    newGuestRef: React.RefObject<any>;
    handleSubmit: (guestId: string, values: any) => void;
    onCancelEdit: (guestId: string) => void;
    setGuestToRemove: (guest: TGuest) => void;
    setNewGuestIsOpen: (isOpen: boolean) => void;
    handleNewGuestSubmit: (values: any) => void;
    customerOptions?: TCustomerOptions;
    isMobile?: boolean;
};

export const GuestForm: React.FC<GuestFormProps> = ({
    guestToEdit,
    newGuestIsOpen,
    guests,
    updatingGuestInformationInProgress,
    creatingNewGuestGuestInProgress,
    guestRefs,
    newGuestRef,
    handleSubmit,
    onCancelEdit,
    setGuestToRemove,
    setNewGuestIsOpen,
    handleNewGuestSubmit,
    customerOptions,
    isMobile = false,
}) => {
    const t = useTranslate();

    return (
        <div id="edit-guest-form">
            {guests
                .filter(([guestId]) => guestId === guestToEdit)
                .map(([guestId, guest]) => {
                    return (
                        <section
                            key={guestId}
                            id={`edit-guest-form-${guestId}`}
                            aria-labelledby={`guest-${guestId}`}
                            className={classNames("u-bg-light-grey u-p-24 u-br-8", {
                                "u-disabled": updatingGuestInformationInProgress,
                            })}
                            tabIndex={-1}
                        >
                            <Heading
                                type="h3"
                                styleAs="default-highlighted"
                                className="u-mb-18"
                            >{`Redigerar ${guest.firstname} ${guest.lastname}`}</Heading>
                            <PersonalInformation
                                ref={guestRefs[guestId].form}
                                isPrimaryGuest={false}
                                guest={guest as TPersonalInformationGuest}
                                onSubmit={values => {
                                    handleSubmit(guestId, values);
                                }}
                                showAgePicker
                                showKeycardNo={customerOptions?.show_keycard_number || false}
                                showKeycardImage={customerOptions?.show_keycard_image || false}
                                showWeight={customerOptions?.show_weight || false}
                                showHeight={customerOptions?.show_height || false}
                                showShoesize={customerOptions?.show_shoesize || false}
                                showHeadsize={customerOptions?.show_headsize || false}
                                uploadPhotoButtonType="transparent"
                            />
                            <div className="u-d-flex u-flex-wrap u-gap-18 u-justify-content-end">
                                <Button
                                    type={isMobile ? "transparent" : "tertiary"}
                                    onClick={() => onCancelEdit(guestId)}
                                    fullWidth={isMobile}
                                >
                                    {t("book.general.cancel")}
                                </Button>
                                <Button type="danger" onClick={() => setGuestToRemove(guest)} fullWidth={isMobile}>
                                    {t("book.general.remove")}
                                </Button>
                                <Button
                                    submit
                                    type="primary"
                                    onClick={() => guestRefs[guestId].form.current.submitForm()}
                                    loading={updatingGuestInformationInProgress}
                                    fullWidth={isMobile}
                                >
                                    {t("book.general.update")}
                                </Button>
                            </div>
                        </section>
                    );
                })}
            {newGuestIsOpen && (
                <section
                    className={classNames("u-bg-light-grey u-p-24 u-br-8", {
                        "u-disabled": creatingNewGuestGuestInProgress,
                    })}
                >
                    <Heading type="h3" styleAs="default-highlighted" className="u-mb-18">
                        {t("book.checkout.add_guest")}
                    </Heading>
                    <PersonalInformation
                        isPrimaryGuest={false}
                        ref={newGuestRef}
                        onSubmit={handleNewGuestSubmit}
                        showAgePicker
                        showKeycardNo={customerOptions?.show_keycard_number || false}
                        showKeycardImage={customerOptions?.show_keycard_image || false}
                        showWeight={customerOptions?.show_weight || false}
                        showHeight={customerOptions?.show_height || false}
                        showShoesize={customerOptions?.show_shoesize || false}
                        showHeadsize={customerOptions?.show_headsize || false}
                        uploadPhotoButtonType="transparent"
                        newGuest
                    />
                    <div className="u-d-flex u-gap-18 u-justify-content-end">
                        <Button type="tertiary" onClick={() => setNewGuestIsOpen(false)}>
                            {t("book.general.cancel")}
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => newGuestRef.current?.submitForm()}
                            loading={creatingNewGuestGuestInProgress}
                        >
                            {t("book.my_pages.create_guest")}
                        </Button>
                    </div>
                </section>
            )}
        </div>
    );
};
